import React, { useMemo } from 'react';
import { Search, ArrowDown, ArrowUp } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { Checkbox } from './checkbox';
import { Button } from './button';
import { Input } from './input';
import { DataTableColumn, DataTableProps } from './data-table.types';
import { ClampedCellContent } from './clamped-cell-content';

/**
 * Pure visual DataTable component (internal)
 *
 * This is a presentation-only component - all data operations (sorting, filtering,
 * pagination, row selection) are handled by the parent component.
 *
 * Use the main DataTable component with variant="visual" instead of using this directly.
 *
 * @internal
 */
export function DataTableVisual<TData = unknown>({
  columns,
  rows,
  variant = 'default',
  scrollable = false,
  horizontalScroll = false,
  height = '400px',
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search columns...',
  enableSearch = false,
  sortedColumn,
  sortDirection,
  onSort,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  enablePagination = false,
  paginationLeftSlot,
  paginationRightSlot,
  selectedRows,
  onRowSelect,
  onSelectAll,
  getRowId = (_, index) => String(index),
  enableRowSelection = false,
  enableExport,
  exportFilename,
  extraExportOptions,
  bordered,
  rowBordered = true,
  onRowClick,
  cellOverflow = 'wrap',
  maxLines = 3,
  loading = false,
  loadingText = 'Loading...',
  emptyText = 'No results.',
  className = '',
}: DataTableProps<TData>) {
  // Add selection column if row selection is enabled
  const allColumns = useMemo<DataTableColumn<TData>[]>(() => {
    if (!enableRowSelection) return columns;

    const isAllSelected = rows.length > 0 && rows.every((row, idx) =>
      selectedRows?.has(getRowId(row, idx))
    );
    const isSomeSelected = rows.some((row, idx) =>
      selectedRows?.has(getRowId(row, idx))
    );

    // If the first data column is pinned left, also pin the selection column
    const firstDataColumnPinnedLeft = columns[0]?.pinned === 'left';

    const selectionColumn: DataTableColumn<TData> = {
      id: 'select',
      header: (
        <Checkbox
          checked={isAllSelected ? true : (isSomeSelected ? 'indeterminate' : false)}
          onCheckedChange={(checked) => onSelectAll?.(!!checked)}
          aria-label="Select all"
        />
      ),
      cell: (row, rowIndex) => {
        const rowId = getRowId(row, rowIndex);
        return (
          <Checkbox
            checked={selectedRows?.has(rowId)}
            onCheckedChange={(checked) => onRowSelect?.(rowId, !!checked)}
            aria-label="Select row"
          />
        );
      },
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      ...(firstDataColumnPinnedLeft && { pinned: 'left' as const }),
    };

    return [selectionColumn, ...columns];
  }, [columns, enableRowSelection, rows, selectedRows, getRowId, onRowSelect, onSelectAll]);

  // Render sort button for column header
  const renderSortButton = (column: DataTableColumn<TData>) => {
    if (!column.sortable) return null;

    const isSorted = sortedColumn === column.id;
    const isAsc = isSorted && sortDirection === 'asc';
    const isDesc = isSorted && sortDirection === 'desc';

    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 hover:bg-gray-100"
        onClick={() => onSort?.(column.id)}
        type="button"
      >
        {isDesc ? (
          <ArrowDown className="h-4 w-4" />
        ) : isAsc ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowUp className="h-4 w-4 opacity-40" />
        )}
      </Button>
    );
  };

  // Get column styles based on pinning and sizing
  const getColumnStyles = (column: DataTableColumn<TData>) => {
    const styles: React.CSSProperties = {};

    if (column.width) styles.width = typeof column.width === 'number' ? `${column.width}px` : column.width;
    if (column.minWidth) {
      styles.minWidth = typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth;
    } else if (horizontalScroll) {
      // Default minimum width for horizontal-scroll tables to prevent congested columns
      styles.minWidth = '120px';
    }
    if (column.maxWidth) styles.maxWidth = typeof column.maxWidth === 'number' ? `${column.maxWidth}px` : column.maxWidth;

    return styles;
  };

  // Pre-compute left offsets for stacked left-pinned columns
  const pinnedLeftOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let cumulativeLeft = 0;
    for (const col of allColumns) {
      if (col.pinned === 'left') {
        offsets.set(col.id, cumulativeLeft);
        cumulativeLeft += (typeof col.width === 'number' ? col.width : 0);
      }
    }
    return offsets;
  }, [allColumns]);

  // Check if a left-pinned column is the last one (to show border/shadow only on the outermost edge)
  const lastPinnedLeftId = useMemo(() => {
    let last = '';
    for (const col of allColumns) {
      if (col.pinned === 'left') last = col.id;
    }
    return last;
  }, [allColumns]);

  // Get pinned column classes
  const getPinnedClasses = (column: DataTableColumn<TData>, isHeader: boolean) => {
    if (!column.pinned) return '';

    const baseClasses = 'sticky z-20';
    const bgClass = isHeader
      ? (variant === 'minimal' ? 'bg-white' : 'bg-gray-200')
      : (variant === 'minimal' ? 'bg-white' : 'bg-gray-50');
    // Only show border/shadow on the last left-pinned column (outermost edge)
    const isLastPinnedLeft = column.pinned === 'left' && column.id === lastPinnedLeftId;
    const borderClass = column.pinned === 'left'
      ? (isLastPinnedLeft ? 'border-r-2 border-gray-300 shadow-md' : '')
      : 'border-l-2 border-gray-300 shadow-md';

    return `${baseClasses} ${bgClass} ${borderClass}`;
  };

  // Get pinned column inline styles (for left offset)
  const getPinnedStyles = (column: DataTableColumn<TData>): React.CSSProperties => {
    if (!column.pinned) return {};
    if (column.pinned === 'right') return { right: 0 };
    const offset = pinnedLeftOffsets.get(column.id) ?? 0;
    return { left: offset };
  };

  // Variant-specific classes. When rowBordered is false we emit `border-b-0`
  // so tailwind-merge strips the `border-b` baked into TableRow's CVA variants
  // (see packages/ui-core/src/components/ui/table.tsx). Just omitting the
  // class isn't enough — CVA applies it first and our className merges after.
  const bodyRowBorderClass = rowBordered
    ? variant === 'minimal'
      ? 'border-b'
      : 'border-b border-gray-200'
    : 'border-b-0';
  const variantClasses = {
    header: variant === 'minimal'
      ? 'bg-white'
      : 'bg-gray-200/75 backdrop-blur supports-[backdrop-filter]:bg-gray-200/75',
    headerRow: variant === 'minimal'
      ? 'border-b'
      : 'border-b border-gray-200 hover:bg-transparent',
    headerCell: variant === 'minimal'
      ? 'font-semibold py-3 px-3'
      : 'h-12 px-3 text-left align-middle font-medium text-gray-700',
    bodyRow: variant === 'minimal'
      ? `${bodyRowBorderClass} hover:bg-gray-100`.trim()
      : `${bodyRowBorderClass} transition-colors hover:bg-gray-50/50`.trim(),
    bodyCell: variant === 'minimal'
      ? 'py-3 px-3'
      : 'p-3 align-middle',
  };

  // Render table content
  const renderTable = () => (
    <Table variant={variant} enableExport={enableExport} exportFilename={exportFilename} extraExportOptions={extraExportOptions} className={`${horizontalScroll ? 'w-max min-w-full' : ''}${cellOverflow === 'truncate' ? ' table-fixed' : ''}`}>
      <TableHeader variant={variant} className={`sticky top-0 z-10 ${variantClasses.header}`}>
        <TableRow variant={variant} className={variantClasses.headerRow}>
          {allColumns.map((column) => (
            <TableHead
              variant={variant}
              key={column.id}
              className={`${variantClasses.headerCell} ${getPinnedClasses(column, true)} ${
                column.id === 'select' ? '[&:has([role=checkbox])]:pr-0' : ''
              }`}
              style={{ ...getColumnStyles(column), ...getPinnedStyles(column) }}
            >
              <div className="flex items-center gap-2">
                {column.header}
                {renderSortButton(column)}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableRow variant={variant}>
            <TableCell
              variant={variant}
              colSpan={allColumns.length}
              className="h-24 text-center text-sm text-gray-500"
            >
              {loadingText}
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow variant={variant}>
            <TableCell
              variant={variant}
              colSpan={allColumns.length}
              className="h-24 text-center text-sm text-gray-500"
            >
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, rowIndex) => {
            const rowId = getRowId(row, rowIndex);
            const isSelected = selectedRows?.has(rowId);

            return (
              <TableRow
                variant={variant}
                key={rowId}
                className={`${variantClasses.bodyRow}${onRowClick ? ' cursor-pointer' : ''}`}
                data-state={isSelected ? 'selected' : undefined}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
              >
                {allColumns.map((column) => (
                  <TableCell
                    variant={variant}
                    key={`${rowId}-${column.id}`}
                    className={`${variantClasses.bodyCell} ${getPinnedClasses(column, false)} ${
                      column.id === 'select' ? '[&:has([role=checkbox])]:pr-0' : ''
                    }`}
                    style={{
                      ...getColumnStyles(column),
                      ...getPinnedStyles(column),
                      ...(cellOverflow === 'truncate' ? {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      } : {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }),
                    }}
                  >
                    {cellOverflow === 'wrap' && column.clamp !== false && column.id !== 'select' ? (
                      <ClampedCellContent maxLines={maxLines}>
                        {column.cell(row, rowIndex)}
                      </ClampedCellContent>
                    ) : (
                      column.cell(row, rowIndex)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search UI */}
      {enableSearch && (
        <div className="flex items-center gap-2">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500" size={15} />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full pl-8"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className={bordered === false ? 'overflow-hidden' : 'rounded-md border border-gray-200 overflow-hidden'}>
        {scrollable || horizontalScroll ? (
          <div className="relative" style={{ height: scrollable ? height : 'auto' }}>
            <div
              className={horizontalScroll ? 'overflow-x-auto scrollbar-visible' : ''}
              style={{
                overflowX: horizontalScroll ? 'scroll' : 'auto',
                overflowY: scrollable ? 'auto' : 'visible',
                scrollbarGutter: 'stable',
                WebkitOverflowScrolling: 'touch',
                width: '100%',
              }}
            >
              {renderTable()}
            </div>
          </div>
        ) : (
          renderTable()
        )}
      </div>

      {/* Pagination UI */}
      {enablePagination && totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 py-4">
          <div className="flex items-center text-sm text-gray-600 min-w-0">
            {paginationLeftSlot}
          </div>
          <div className="flex items-center space-x-2 shrink-0">
            {paginationRightSlot}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="h-8 w-8 p-0 lg:w-auto lg:px-3"
              type="button"
            >
              <span className="hidden lg:inline">Previous</span>
              <span className="lg:hidden">‹</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="h-8 w-8 p-0 lg:w-auto lg:px-3"
              type="button"
            >
              <span className="hidden lg:inline">Next</span>
              <span className="lg:hidden">›</span>
            </Button>
            <span className="flex items-center gap-1 text-sm text-gray-600">
              <div>Page</div>
              <strong>
                {currentPage} of {totalPages}
              </strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
